// @see: https://docs.google.com/document/d/1VxDMC7SRr9z9mCA9tbVYya9JnWGQ_m2LuS15f5SVw4U/edit?tab=t.0#heading=h.pt26kn1ohxnw
// @see: https://developer.android.com/reference/android/webkit/WebView
// @see: https://developer.android.com/develop/ui/views/layout/webapps/webview#BindingJavaScript
// @see: https://developer.apple.com/documentation/webkit/wkwebview
// @see: https://developer.apple.com/documentation/webkit/wkscriptmessagehandler
//
// @local-testing: To simulate native app communication in the browser console, paste the following
// before navigating to any route. Replace access-token with a fresh value from the API.
//
// window.nativeApp = {
//   postMessage: (message) => {
//     let key, data
//     try {
//       const parsed = typeof message === 'string' && message.startsWith('{') ? JSON.parse(message) : null
//       key = parsed ? parsed.type : message
//       data = parsed?.data
//     } catch { key = message }
//     const map = {
//       'access-token':           'REPLACE_WITH_FRESH_TOKEN',
//       'language':               'ar',
//       'user-id':                '373',
//       'device-model':           'iPhone12,1',
//       'device-os-version':      '17',
//       'app-version':            '266',
//       'ride-id':                null,
//       'location':               [24.818594, 46.788464],
//       'client-profile-updated': null,
//       'change-language-ar':     null,
//       'change-language-en':     null,
//       'signout':                null,
//       'user-logged':            null,
//     }
//     console.log('[nativeApp]', key, data ? '| data:' : '', data ?? '', '→', map[key] ?? null)
//     return Promise.resolve(map[key] ?? null)
//   }
// }
// window.webkit = { messageHandlers: { nativeApp: window.nativeApp } }
// window.location.href = 'http://localhost:8080/#/driver/login?role=driver'
import * as appCommunicationTypes from '@/types/app-communication'
import typeCheckerUtils from '@/utils/type-checker'

const initState = {
  isInitialized: false,
  isInitializing: false,
  retryCount: 0,
  lastInitAttempt: 0
}

const CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000
}

// @note: `nativeApp` must be set by the app developers on both iOS and Android when creating and initializing the WebView.
function isAppCommunicationAvailable(): boolean {
  return !!(window.webkit?.messageHandlers?.nativeApp || window.nativeApp)
}

async function initializeCommunication(
  maxRetries = CONFIG.MAX_RETRIES,
  retryDelay = CONFIG.RETRY_DELAY
): Promise<void> {
  console.log('initializeCommunication')
  // Prevent concurrent initialization
  if (initState.isInitializing) {
    console.log('initializeCommunication - concurrent initialization')
    return
  }

  // Prevent too frequent retries so between retries there is a delay which is by default 1 second
  const now = Date.now()
  if (now - initState.lastInitAttempt < retryDelay) {
    console.log('initializeCommunication - too frequent retries')
    return
  }

  initState.isInitializing = true
  initState.lastInitAttempt = now

  if (isAppCommunicationAvailable()) {
    console.log('Native Communication Used')
    initState.isInitialized = true
    return
  }

  if (initState.retryCount < maxRetries) {
    initState.isInitializing = false
    initState.retryCount++

    console.log('initializeCommunication - retry', initState.retryCount)

    await new Promise((resolve) => setTimeout(resolve, retryDelay))

    return await initializeCommunication(maxRetries, retryDelay)
  }

  initState.isInitializing = false
  initState.isInitialized = false
  throw new Error('no_communication_used_with_mobile_after_retries')
}

// @note: The message can be either:
// 1. A string — must be one of the values defined in the `AppMessage` enum (src/app-communication).
// 2. An object — must contain:
//    - `type`: a string that matches one of the `AppMessage` enum values.
//    - `data`: the payload to be sent to the app.
async function sendRequestToApp(
  message:
    | appCommunicationTypes.AppMessage
    | { type: appCommunicationTypes.AppMessage; data: Record<string, string> }
): Promise<string | number[] | null> {
  if (isAppCommunicationAvailable()) {
    if (typeof message === 'string') {
      const responseBody = window.webkit?.messageHandlers?.nativeApp?.postMessage
        ? await window.webkit.messageHandlers.nativeApp.postMessage(message)
        : window.nativeApp?.postMessage(message)

      return responseBody ?? null
    } else if (typeof message === 'object' && 'type' in message && 'data' in message) {
      const responseBody = window.webkit?.messageHandlers?.nativeApp?.postMessage
        ? await window.webkit.messageHandlers.nativeApp.postMessage(message)
        : window.nativeApp?.postMessage(JSON.stringify(message))

      return responseBody ?? null
    } else {
      console.error('wrong message passed', message)
      return null
    }
  } else {
    console.error('app communication not available try to send message', message)

    return null
  }
}

const isAndroid = () => !!window.nativeApp
const isIOS = () => !!window.webkit?.messageHandlers?.nativeApp

export default {
  initializeCommunication,
  getLocation: async () => {
    try {
      const currentLocation = await sendRequestToApp(appCommunicationTypes.AppMessage.Location)

      return typeCheckerUtils.isArray(currentLocation) ? currentLocation : []
    } catch {
      return []
    }
  },
  getLanguage: async () => {
    try {
      const language = await sendRequestToApp(appCommunicationTypes.AppMessage.Language)

      // sometimes the mobile returns invalid language value, such as the language of the device is not supported by the app such as urdu
      //  check this https://ego.sentry.io/issues/6615338155/?environment=production&project=1306132&query=&referrer=issue-stream&stream_index=0
      if (typeCheckerUtils.isValidLanguage(language)) {
        return language as string
      } else {
        return 'ar'
      }
    } catch (error) {
      console.error('unexpected error when retrieve language', error)

      return 'ar'
    }
  },
  getAccessToken: async () => {
    const accessToken = await sendRequestToApp(appCommunicationTypes.AppMessage.AccessToken)

    console.assert(typeCheckerUtils.isNotEmptyString(accessToken), accessToken)

    return accessToken as string
  },
  getUserId: async () => {
    const userId = await sendRequestToApp(appCommunicationTypes.AppMessage.UserId)

    console.assert(typeCheckerUtils.isStringNumber(userId), userId)

    return userId as string
  },
  getDeviceModel: async () => {
    const deviceModel = await sendRequestToApp(appCommunicationTypes.AppMessage.DeviceModel)

    console.assert(typeCheckerUtils.isNotEmptyString(deviceModel), deviceModel)

    return deviceModel as string
  },
  getDeviceOSVersion: async () => {
    if (isAndroid()) {
      const deviceOSVersion = await sendRequestToApp(
        appCommunicationTypes.AppMessage.DeviceOSVersion
      )

      console.assert(typeCheckerUtils.isNotEmptyString(deviceOSVersion), deviceOSVersion)

      return deviceOSVersion as string
    }
  },
  getAppVersion: async () => {
    const appVersion = await sendRequestToApp(appCommunicationTypes.AppMessage.AppVersion)

    console.assert(typeCheckerUtils.isNotEmptyString(appVersion), appVersion)

    return appVersion as string
  },
  getDevice: async () => {
    if (isAndroid()) {
      return 'android'
    } else if (isIOS()) {
      return 'ios'
    } else {
      console.error('device not ios or android')
    }
  },
  informClientProfileUpdated: async () => {
    await sendRequestToApp(appCommunicationTypes.AppMessage.ClientProfileUpdated)
  },
  informChangeLanguage: async (newLanguage: string) => {
    if (newLanguage === 'ar') {
      await sendRequestToApp(appCommunicationTypes.AppMessage.ChangeLanguageAR)
    } else if (newLanguage === 'en') {
      await sendRequestToApp(appCommunicationTypes.AppMessage.ChangeLanguageEN)
    } else {
      console.error('wrong passed language', newLanguage)
    }
  },
  informSignout: async () => {
    await sendRequestToApp(appCommunicationTypes.AppMessage.Signout)
  },
  informUserLogged: async (data: { accessToken: string; userId: string }) => {
    if (
      typeCheckerUtils.isNotEmptyString(data.accessToken) &&
      typeCheckerUtils.isStringNumber(data.userId)
    ) {
      await sendRequestToApp({
        type: appCommunicationTypes.AppMessage.UserLogged,
        data: {
          accessToken: data.accessToken,
          userId: data.userId
        }
      })
    } else {
      console.error('wrong user-info passed', data.accessToken, data.userId)
    }
  },
  isAndroid,
  isIOS,
  isAppCommunicationAvailable
}
